import React, { useContext, useRef } from 'react';
import styled from 'styled-components';

import { permissionChecks } from '../../../utils/permission-check';
import { ViewSettings } from '../../../viewer/view-settings';
import { viewerZIndices } from '../../../viewer/viewer-z-indices';
import { SkyviewContext } from '../skyview/skyview-state';
import { CameraControlToggle } from './camera-control-toggle';
import { FocusSceneButton } from './focus-scene-button';
import { OrientationCube } from './orientation-cube';
import { FlexibleCameraTool } from './toolbar/tools/view-settings/flexible-camera-tool';
import { useViewSettings } from './toolbar/tools/view-settings/use-view-settings';
import { ViewSettingsTool } from './toolbar/tools/view-settings/view-settings-tool';

/**
 * The orientation cube, with the cameras and the view settings below it in place of the toolbar's
 * views: in the CAD viewer and the flexible viewing mode. The CAD viewer has no view settings,
 * having no layers.
 */
const CameraViews = () => {
  const { activeScene } = useContext(SkyviewContext);
  // Re-renders when the camera or the viewing mode changes
  const { camera } = useViewSettings();
  const ref = useRef<HTMLDivElement>(null);
  const isCadViewer = ViewSettings.instance.isCadViewer;

  if (activeScene !== 'skyviewScene' || !ViewSettings.instance.showsOrientationCube) {
    return null;
  }

  // The timeline data needs access to the views that show it, as in the toolbar's classic views
  const hasPerspective =
    isCadViewer ||
    permissionChecks.hasAccessToFeature('feature:viewer:toolbar:tiled_model_scene') ||
    permissionChecks.hasAccessToFeature('feature:viewer:toolbar:point_cloud_scene');
  const hasOrthographic =
    isCadViewer || permissionChecks.hasAccessToFeature('feature:viewer:toolbar:ortho_tile_scene');
  const hasCameraControl =
    hasPerspective && camera === 'perspective' && ViewSettings.instance.canChooseCameraControl;
  // The CAD viewer has no timeline data to focus on
  const hasFocus =
    !isCadViewer && permissionChecks.hasAccessToFeature('feature:viewer:toolbar:focus');
  const hasViewSettings =
    !isCadViewer && ViewSettings.instance.isEnabled && (hasPerspective || hasOrthographic);

  return (
    <Component ref={ref}>
      <OrientationCube />
      {/* The view settings open below it all, or beside it where they don't fit below */}
      <Controls>
        <Panel>
          {hasPerspective && <FlexibleCameraTool camera="perspective" />}
          {hasOrthographic && <FlexibleCameraTool camera="orthographic" />}
          {/* Into the view, clear of the right menu beside it */}
          {hasViewSettings && <ViewSettingsTool placement="bottom-end" toolbarRef={ref} />}
        </Panel>
        {(hasFocus || hasCameraControl) && (
          <RoundButtons>
            {hasFocus && <FocusSceneButton />}
            {hasCameraControl && <CameraControlToggle />}
          </RoundButtons>
        )}
      </Controls>
    </Component>
  );
};

// At the top right, below the share and On demand chips, as the timeline bar's panel opens
const Component = styled.div`
  position: absolute;
  top: 62px;
  right: 16px;
  z-index: ${viewerZIndices.toolbar};
  width: max-content;
  pointer-events: auto;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
`;

const RoundButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

// Dark and slightly translucent, as the tooltips, so the icons show on any background without
// standing out. The active camera in the brand's yellow.
const Panel = styled.div`
  display: flex;
  gap: 2px;
  padding: 4px;
  border-radius: 0.5em;
  background-color: color-mix(
    in srgb,
    ${(props) => props.theme.color.gray.darkest} 80%,
    transparent
  );
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);

  // The tool icons, which are styled for the white toolbar
  > span {
    padding: 4px;
    color: #fff;
    background-color: transparent;
  }

  // Smaller than the toolbar's
  > span > svg {
    font-size: 16px;
  }

  > span:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  > span:has(> [data-active='true']) {
    color: ${(props) => props.theme.color.brand.dark};
  }
`;

export { CameraViews, Component as CameraViewsStyled };
